import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';

const SatDataGrid = forwardRef((props, ref) => {
  const {
    columns,
    dataSource,
    sortingOption,
    setRowStyle,
    width = '',
    defaultStyle = 'tbl-sty1',
    customStyleClass,
    noDataRender,
    useHeaderPinned = false,
    gridId = 'gridScroll', // 수정
    onSortChange,
    onRowSelection,
    onScroll,
    tableLayoutFixed = false,
  } = props;

  const [rowData, setRowData] = useState([]);
  const [currentMaxHeight, setCurrentMaxHeight] = useState(0);

  useImperativeHandle(ref, () => ({
    // 그리드의 현재 행 데이터(rowData)
    getRowData() {
      return rowData;
    },
  }));

  useEffect(() => {
    setRowData(
      dataSource.map((data, index) => {
        return { ...data, rowIndex: index };
      }),
    );
  }, []);

  useEffect(() => {
    //새로고침
    if (rowData.length > dataSource.length) {
      setCurrentMaxHeight(0);
    }
    setRowData(
      dataSource.map((data, index) => {
        return { ...data, rowIndex: index };
      }),
    );
  }, [dataSource]);

  const getRowStyle = row => {
    if (typeof setRowStyle === 'function') {
      return setRowStyle(row);
    } else if (typeof setRowStyle === 'string') {
      return setRowStyle;
    } else {
      return '';
    }
  };

  const HeaderRender = ({ column }) => {
    const {
      dataIndex,
      sortable = false,
      title,
      width = '',
      headerColSpan,
      customHeader,
    } = column;

    const getSortOption = () => {
      const sortOption = sortingOption.find(
        option => option.sortBy === dataIndex,
      );
      if (sortable && sortOption) {
        return sortOption.order === 'asc' ? 'sort-down up' : 'sort-down';
      } else {
        return '';
      }
    };

    const handleHeaderSorting = () => {
      const sortOption = sortingOption.find(
        option => option.sortBy === dataIndex,
      );

      if (sortOption) {
        onSortChange({
          ...sortOption,
          order: sortOption.order === 'asc' ? 'desc' : 'asc',
        });
      }
    };

    return (
      <th style={{ width: width }} colSpan={headerColSpan || ''}>
        <span className={getSortOption(column)} onClick={handleHeaderSorting}>
          {typeof customHeader === 'function' ? customHeader(column) : title}
        </span>
      </th>
    );
  };

  const CellRender = useCallback(({ row, column }) => {
    const {
      dataIndex,
      render,
      align = '',
      colSpan = 1,
      rowSpan = 1,
      width = '',
      setColumnStyle = '',
    } = column;

    const getColumnStyle = () => {
      let className = '';

      if (align !== '') className += `text-${align} `;
      if (setColumnStyle !== '') className += `${setColumnStyle} `;

      return className;
    };

    return (
      <td
        className={getColumnStyle()}
        colSpan={
          typeof colSpan === 'function' ? colSpan(row[dataIndex], row) : colSpan
        }
        rowSpan={
          typeof rowSpan === 'function' ? rowSpan(row[dataIndex], row) : rowSpan
        }
        style={{ width: width }}
      >
        {typeof render === 'function'
          ? render(row[dataIndex], row)
          : row[dataIndex]}
      </td>
    );
  }, []);

  const rowSpanRender = row => {
    const filterRows = columns.find(
      column =>
        (typeof column.rowSpan === 'function'
          ? column.rowSpan(row[column.dataIndex], row)
          : column.rowSpan) > 1 || column?.useRowSpanRenderer,
    );
    if (filterRows) {
      return filterRows.rowSpanRenderer(row);
    } else {
      return <></>;
    }
  };

  const handleScroll = () => {
    if (onScroll && rowData.length > 0) {
      const scrollTarget = document.getElementById(gridId); // 수정
      const scrollPosition = scrollTarget.scrollTop + scrollTarget.clientHeight;
      const scrollEventHeight = scrollTarget.scrollHeight - 1;

      if (
        scrollPosition >= scrollEventHeight &&
        scrollPosition > currentMaxHeight
      ) {
        onScroll(true);
        setCurrentMaxHeight(scrollTarget.scrollHeight); //MAX
      } else {
        onScroll(false);
      }
    }
  };

  return (
    <div
      id={gridId} // 수정
      className={`${useHeaderPinned ? 'thead-fix-scroll' : ''} ${width}`}
      onScroll={handleScroll}
    >
      <table
        className={`${defaultStyle} ${customStyleClass}`}
        style={{ tableLayout: tableLayoutFixed ? 'fixed' : '' }}
      >
        <thead>
          <tr style={{ whiteSpace: 'pre-line' }}>
            {columns.map((column, index) => {
              const { headerColSpan, isNoHeader } = column;

              return (
                !isNoHeader && (
                  <HeaderRender
                    key={index}
                    column={column}
                    headerColSpan={headerColSpan}
                  />
                )
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rowData.length > 0 ? (
            rowData.map((data, index) => (
              <Fragment key={index}>
                <tr
                  className={getRowStyle(data)}
                  onClick={() =>
                    typeof onRowSelection === 'function' && onRowSelection(data)
                  }
                >
                  {/* TODO Refactoring require */}
                  {columns.map((columnInfo, index) => {
                    const { key, colSpan = 1, dataIndex, render } = columnInfo;
                    const colSpanRows = columns.filter(
                      col =>
                        Object.keys(col).includes('colSpan') &&
                        col.colSpan(data[dataIndex], data) > 1,
                    );
                    if (colSpanRows.length > 0) {
                      const colSpanIndex = columns.findIndex(col =>
                        Object.keys(col).includes('colSpan'),
                      );
                      if (colSpanIndex > index) {
                        return (
                          <CellRender
                            key={key}
                            row={data}
                            column={columnInfo}
                          />
                        );
                      } else {
                        if (colSpanIndex === index) {
                          return (
                            <td
                              key={key}
                              colSpan={colSpan(data[dataIndex], data)}
                            >
                              {render(data[dataIndex], data)}
                            </td>
                          );
                        }
                      }
                    } else {
                      return (
                        <CellRender key={key} row={data} column={columnInfo} />
                      );
                    }
                  })}
                </tr>
                {rowSpanRender(data)}
              </Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="nodata">
                {typeof noDataRender === 'string' && noDataRender}
                {typeof noDataRender === 'function' && noDataRender()}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

SatDataGrid.propTypes = {
  /**
   * 그리드 칼럼
   */
  columns: PropTypes.array,
  /**
   * 그리드 데이터
   */
  dataSource: PropTypes.array,
  /**
   * 행 style
   */
  setRowStyle: PropTypes.any,
  /**
   * sorting Option
   */
  sortingOption: PropTypes.array,
  /**
   * table class 추가
   */
  customStyleClass: PropTypes.string,
  /**
   * 그리드 데이터 없을 시 메세지
   */
  noDataRender: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * drag & drop option
   */
  draggable: PropTypes.bool,
  /**
   * 행 선택(rowSelection) callback event
   */
  onRowSelection: PropTypes.func,
  /**
   * 헤더 정렬 헤더 클릭시 callback event
   */
  onSortChange: PropTypes.func,
  /**
   * 입력값 callback event
   */
  onChange: PropTypes.func,
};

SatDataGrid.defaultProps = {
  columns: [],
  dataSource: [],
  setRowStyle: () => {},
  sortingOption: [],
  draggable: false,
  customStyleClass: '',
  onRowSelection: () => {},
  onSortChange: () => {},
  onChange: () => {},
};

export default SatDataGrid;
